import gsap from 'gsap'
import { nextTick } from 'vue'

// Tap feedback for the floating nav pill/circle (Mail-style) — a quick
// scale-down on press and a spring back on release. Deliberately a plain
// ease-out, not the app's other overshoot-bounce keyframe animation
// (see nav-bounce in base.css), per the agreed spec: 120-150ms, no overshoot.
const PRESS_SCALE = 0.95
const PRESS_DURATION = 0.13

export function pressNavElementDown(el: HTMLElement | null | undefined): void {
  if (!el) return
  gsap.to(el, { scale: PRESS_SCALE, duration: PRESS_DURATION, ease: 'power2.out' })
}

export function pressNavElementUp(el: HTMLElement | null | undefined): void {
  if (!el) return
  gsap.to(el, { scale: 1, duration: PRESS_DURATION, ease: 'power2.out' })
}

// Shared morph timing for every compact <-> expanded transition in the nav
// (toggle grow-pulse, search width-morph) — one GSAP configuration instead of
// each call site tuning its own tween.
const MORPH_DURATION = 0.3
const MORPH_EASE = 'back.out(1.4)'

// Toggle's grow-on-tap hint (Phase 3, decision: "kun 'grow' når man trykker
// på den, men falder tilbage på samme størrelse efter trykket") — a single
// tap-triggered pulse, not a press-and-hold state like pressNavElementDown/Up.
export function pulseGrowNavElement(el: HTMLElement | null | undefined): void {
  if (!el) return
  gsap
    .timeline()
    .to(el, { scale: 1.12, duration: 0.15, ease: 'power2.out' })
    .to(el, { scale: 1, duration: MORPH_DURATION, ease: MORPH_EASE })
}

// FLIP-style width morph: measures the element's width, lets the caller
// mutate reactive state (which changes layout instantly via Vue/CSS), then
// tweens from the old width to the new one instead of snapping — used for
// the search pill expanding to take over the toggle pill's freed space.
// `clearProps: 'width'` drops the inline style afterward so flexbox governs
// sizing again once settled (e.g. on window resize).
export async function morphWidthChange(
  el: HTMLElement | null | undefined,
  applyStateChange: () => void
): Promise<void> {
  if (!el) {
    applyStateChange()
    return
  }

  const startWidth = el.getBoundingClientRect().width
  applyStateChange()
  await nextTick()
  const endWidth = el.getBoundingClientRect().width

  gsap.fromTo(
    el,
    { width: startWidth },
    { width: endWidth, duration: MORPH_DURATION, ease: MORPH_EASE, clearProps: 'width' }
  )
}

export type TNavTransitionDirection = 'forward' | 'back' | 'lateral'

// Native-feeling page transitions (Phase 5) — a push/pop slide for
// hierarchical navigation (drilling into/out of a project, order, form), and
// a plain crossfade for lateral moves between sibling tabs (Projects ⇄
// Orders), where a directional slide wouldn't make sense. Both the entering
// and leaving page animate at once (not sequential out-in) for a native
// push/pop feel — the leaving page is pulled out of layout flow (position:
// absolute) by the caller's `<Transition>` wrapper so the entering page can
// lay out normally underneath/beside it.
const PAGE_TRANSITION_DURATION = 0.32
const PAGE_TRANSITION_EASE = 'power2.out'

export function animatePageEnter(
  el: HTMLElement,
  done: () => void,
  direction: TNavTransitionDirection
): void {
  if (direction === 'lateral') {
    gsap.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.22, ease: 'power1.out', onComplete: done })
    return
  }

  gsap.fromTo(
    el,
    { xPercent: direction === 'forward' ? 100 : -100 },
    {
      xPercent: 0,
      duration: PAGE_TRANSITION_DURATION,
      ease: PAGE_TRANSITION_EASE,
      clearProps: 'transform',
      onComplete: done,
    }
  )
}

export function animatePageLeave(
  el: HTMLElement,
  done: () => void,
  direction: TNavTransitionDirection
): void {
  el.style.position = 'absolute'
  el.style.inset = '0'
  el.style.width = '100%'

  if (direction === 'lateral') {
    gsap.to(el, { opacity: 0, duration: 0.18, ease: 'power1.in', onComplete: done })
    return
  }

  gsap.to(el, {
    xPercent: direction === 'forward' ? -30 : 30,
    duration: PAGE_TRANSITION_DURATION,
    ease: PAGE_TRANSITION_EASE,
    onComplete: done,
  })
}

// Bottom-nav layout morph (Phase 5) — CustomerNav.vue's default/order-detail/
// project-detail layouts are structurally different DOM (not the same
// elements resized), so this is a crossfade+scale rather than a true FLIP
// morph between shapes. Shares timing with the toggle/search morphs above
// for consistency, but is its own function since it also needs the
// position:absolute leave handling used by the page transition.
export function animateNavLayoutEnter(el: HTMLElement, done: () => void): void {
  gsap.fromTo(
    el,
    { opacity: 0, scale: 0.92 },
    { opacity: 1, scale: 1, duration: MORPH_DURATION, ease: MORPH_EASE, onComplete: done }
  )
}

export function animateNavLayoutLeave(el: HTMLElement, done: () => void): void {
  el.style.position = 'absolute'
  el.style.left = '0'
  el.style.top = '0'

  gsap.to(el, { opacity: 0, scale: 0.92, duration: 0.16, ease: 'power2.in', onComplete: done })
}
